<template>
    <v-card flat>
        <Form ref="elFormObj" as="" v-slot="{ errors: formErrors, meta: formMeta, resetForm }" :initial-values="form">
            <form @submit="onSubmit($event)" @reset.prevent="resetForm()">
                <v-card-text>
                    <v-row density="compact">
                        <v-col>
                            <Field
                                name="contactEmail"
                                label="Email"
                                v-slot="{ field, errors }"
                                v-model="form.contactEmail"
                                :rules="{ email: true, required: true }"
                            >
                                <v-text-field
                                    v-bind="field"
                                    :model-value="field.value"
                                    name="contactEmail"
                                    label="Email"
                                    type="email"
                                    autocorrect="off"
                                    autocapitalize="none"
                                    autocomplete="email"
                                    variant="outlined"
                                    hide-details="auto"
                                    :error-messages="errors"
                                ></v-text-field>
                            </Field>
                        </v-col>
                    </v-row>
                    <v-row density="compact">
                        <v-col class="mt-4">
                            <Field
                                name="orderNo"
                                label="Order Number"
                                v-slot="{ field, errors }"
                                v-model="form.orderNo"
                                :rules="{ required: true }"
                            >
                                <v-text-field
                                    v-bind="field"
                                    :model-value="field.value"
                                    :error-messages="errors"
                                    name="orderNo"
                                    label="Order Number"
                                    variant="outlined"
                                    hide-details="auto"
                                ></v-text-field>
                            </Field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="mr-4"
                        color="primary"
                        type="submit"
                        :disabled="!(formMeta.dirty || formMeta.touched) || !lo_isEmpty(formErrors)"
                        size="large"
                        variant="elevated"
                        block
                    >
                        {{ submitLabel }}
                    </v-btn>
                </v-card-actions>
                <v-card-text>
                    <p>
                        Please contact <CustomerServiceQuickLink>customer service</CustomerServiceQuickLink> for assistance with any questions or concerns.
                    </p>
                </v-card-text>
            </form>
        </Form>
    </v-card>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import type { OrderStatus, OrderStatusModel } from '~/types';
    import { useOrders } from '~/composables';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
    import type { OrderStatusResponse } from '~/types';
    import { initVeeRules } from '~/helpers/forms';
    import CustomerServiceQuickLink from '~/components/support/CustomerServiceQuickLink/CustomerServiceQuickLink.vue';

    initVeeRules([ 'required', 'email' ]);

    const props =  defineProps({
        formValues: {
            type: Object as PropType<Partial<OrderStatus>>,
            default: () => {}
        },
        submitLabel: {
            type: String,
            default: 'Check Order Status'
        }
    });

    const emit = defineEmits<{
        (e: 'on-exit'): void
        (e: 'on-status', value: OrderStatusResponse | Error): void
    }>();

    const useOrdersObj = useOrders();
    const elFormObj = shallowRef<typeof Form>();

    const model: OrderStatusModel = Object.assign({}, {
        orderNo: '',
        contactEmail: '',
        postalCode: ''
    }, props.formValues);

    const form = ref<OrderStatusModel>(model);

    async function onSubmit(e: Event): Promise<void> {
        e.preventDefault();

        const validateObj = await elFormObj.value?.validate();

        if (!validateObj.valid) {
            return;
        }

        const response = await useOrdersObj.getOrderStatus(form.value);

        if (response) {
            emit('on-status', response);
        }
    }

    function exit(): void {
        emit('on-exit');
    }
</script>

<style lang="scss" scoped></style>
